// Verb self-descriptions + the Ask prefill for a conflict — shared by the two conflict surfaces
// (the brief's ConflictResolveModal and the Actions ConflictRoom) so they can never describe the
// same button differently.
//
// The BFF ships the canonical lines on the card response (`verbCopy`/`snoozeCopy`, MVP
// lib/teamwork/verb-copy.ts) and those are rendered VERBATIM — one constant, every surface. The map
// below is only the offline fallback for a SPA deployed ahead of the backend that adds the field;
// it is kept in sync by hand and is never the source of truth when the server answered.

export type ConflictVerbKey =
  | 'decision'
  | 'custom'
  | 'not_conflict'
  | 'object'
  | 'snooze'
  | 'blocked'
  | 'ping'
  | 'route';

/** Mirror of the BFF's CONFLICT_VERB_COPY — used only when the response predates the field. */
export const FALLBACK_VERB_COPY: Record<ConflictVerbKey, string> = {
  decision: 'Records this side as the answer, and tells everyone it touches.',
  custom: 'Records your wording as the answer, and tells everyone it touches.',
  not_conflict: 'Marks these as compatible. It stops coming back, and the call is kept in case it resurfaces.',
  object: 'Reopens a settled conflict and records that you disagree with how it was closed.',
  snooze: 'Hides it from your brief. Nobody else is affected.',
  blocked: "Tells them you're blocked on this. Sends a direct message now, without the decision text.",
  ping: 'Sends them a direct message now, with your note.',
  route: 'Hands it to them to settle. No message is sent — it appears in their next brief as routed by you.',
};

export const FALLBACK_SNOOZE_COPY: Record<'timer' | 'until_inflow', string> = {
  timer: 'Hides it from your brief until the time you pick. Nobody else is affected.',
  until_inflow: 'Hides it from your brief until this disagreement comes up again. Nobody else is affected.',
};

/** The server's line for a verb, falling back to the local mirror when the field is absent. */
export function verbLine(
  serverCopy: Partial<Record<string, string>> | null | undefined,
  verb: ConflictVerbKey,
): string {
  return serverCopy?.[verb] ?? FALLBACK_VERB_COPY[verb];
}

export function snoozeLine(
  serverCopy: Partial<Record<string, string>> | null | undefined,
  mode: 'timer' | 'until_inflow',
): string {
  return serverCopy?.[mode] ?? FALLBACK_SNOOZE_COPY[mode];
}

/** "sent — awaiting Dana" / "routed to Dana" — the state line for an outstanding ping/blocked/route. */
export function awaitingLabel(kind: 'ping' | 'blocked' | 'route', targetName: string | null): string {
  const who = targetName ?? 'a teammate';
  if (kind === 'route') return `routed to ${who} — awaiting them`;
  if (kind === 'blocked') return `blocked on ${who} — awaiting them`;
  return `pinged ${who} — awaiting them`;
}

/**
 * The question a conflict hands to /ask. Carries the headline AND both positions verbatim, because the
 * Ask retrieval is similarity-based: the decision texts are the only tokens that reliably reach the
 * notes behind the disagreement (a bare headline retrieves the conflict card's own wording, not its
 * sources). Prefill only — /ask never auto-submits.
 */
export function askQuestionForConflict(
  title: string,
  positions: Array<{ author?: string | null; text: string }>,
): string {
  const stated = positions
    .filter((p) => p.text?.trim())
    .map((p) => (p.author ? `${p.author} decided “${p.text.trim()}”` : `one side decided “${p.text.trim()}”`))
    .join('; ');
  const head = `What's the background on this disagreement — “${title}”?`;
  return stated ? `${head} ${stated}. What led to each, and what should we go with?` : head;
}
